<template>
  <div class="profile-page">
    <div class="container">
      <!-- 左侧 -->
      <div class="sidebar">
        <div class="profile">
          <div class="avatar">
            <img
              v-if="avatarSrc && !avatarFailed"
              :src="avatarSrc"
              alt="avatar"
              @error="avatarFailed = true"
            >
            <span v-else class="avatar-text">{{ firstChar }}</span>
          </div>
          <div class="name">{{ form.nickname || '用户' }}</div>
          <div class="email">{{ form.email || '未填写邮箱' }}</div>
        </div>

        <div class="menu">
          <div
            class="menu-item"
            :class="{ active: activeSection === 'profile' }"
            @click="activeSection = 'profile'"
          >
            个人资料
          </div>
          <div
            class="menu-item"
            :class="{ active: activeSection === 'ui' }"
            @click="activeSection = 'ui'"
          >
            界面设置
          </div>
          <div
            class="menu-item"
            :class="{ active: activeSection === 'activity' }"
            @click="activeSection = 'activity'"
          >
            用户动态
          </div>
          <div
            class="menu-item"
            :class="{ active: activeSection === 'achievements' }"
            @click="openAchievements()"
          >
            我的成就
          </div>
        </div>
      </div>

      <!-- 右侧 -->
      <div class="content">
        <div class="content-body">
          <!--
            子页面标题：对齐「我的成就」页的「我的成就 / 记录每一个值得纪念的时刻」版式。
            「我的成就」由 AchievementsPage 自己渲染（右侧还要放「已获得 X / Y」），故这里跳过。
          -->
          <div v-if="currentSectionMeta" class="section-head">
            <h2 class="section-title">{{ currentSectionMeta.title }}</h2>
            <p class="section-subtitle">{{ currentSectionMeta.subtitle }}</p>
          </div>

          <!-- 个人资料 -->
          <template v-if="activeSection === 'profile'">
            <div class="info-group">
              <div class="info-row avatar-row">
                <div class="label">修改头像</div>
                <div class="avatar-box">
                  <div class="small-avatar">
                    <img
                      v-if="avatarSrc && !avatarFailed"
                      :src="avatarSrc"
                      alt="avatar"
                      @error="avatarFailed = true"
                    >
                    <span v-else>{{ firstChar }}</span>
                  </div>
                  <button @click="$refs.avatarInput.click()">上传头像</button>
                  <input
                    ref="avatarInput"
                    type="file"
                    accept="image/*"
                    style="display:none"
                    @change="onAvatarChange"
                  >
                </div>
              </div>

              <!-- 账号昵称 -->
              <div class="info-row">
                <div class="label">账号昵称</div>
                <div class="field-wrap">
                  <div class="input-row">
                    <input
                      v-if="!editing.nickname"
                      :value="form.nickname"
                      readonly
                      placeholder="未设置"
                    >
                    <input
                      v-else
                      v-model="editData.nickname"
                      type="text"
                      maxlength="32"
                      placeholder="请输入新昵称"
                    >
                    <div v-if="!editing.nickname" class="input-actions">
                      <span class="input-icon" @click="startEdit('nickname')"><i class="fas fa-pen-to-square"></i></span>
                    </div>
                    <div v-else class="input-actions">
                      <span class="input-icon save" @click="saveNickname"><i class="fas fa-floppy-disk"></i></span>
                      <span class="input-icon cancel" @click="cancelEdit('nickname')"><i class="fas fa-rotate-left"></i></span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 绑定邮箱 -->
              <div class="info-row">
                <div class="label">绑定邮箱</div>
                <div class="field-wrap">
                  <div class="input-row">
                    <input
                      v-if="!editing.email"
                      :value="form.email"
                      readonly
                      placeholder="未绑定"
                    >
                    <input
                      v-else
                      v-model="editData.email"
                      type="email"
                      placeholder="请输入新邮箱"
                    >
                    <div class="input-actions">
                      <span v-if="!editing.email" class="input-icon" @click="startEdit('email')"><i class="fas fa-pen-to-square"></i></span>
                      <span v-else class="input-icon cancel" @click="cancelEdit('email')"><i class="fas fa-rotate-left"></i></span>
                    </div>
                  </div>
                  <div v-if="editing.email" class="code-row">
                    <input v-model="editData.emailCode" type="text" placeholder="请输入验证码" class="code-input">
                    <button
                      class="code-btn"
                      :disabled="codeCooldown.email > 0 || sendingCode.email"
                      @click="sendCode('email')"
                    >
                      {{ sendingCode.email ? '发送中…' : (codeCooldown.email > 0 ? codeCooldown.email + 's' : '获取验证码') }}
                    </button>
                    <button class="code-btn save" :disabled="saving.email" @click="saveEmail">
                      {{ saving.email ? '保存中…' : '保存' }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- 绑定手机 -->
              <div class="info-row">
                <div class="label">绑定手机</div>
                <div class="field-wrap">
                  <div class="input-row">
                    <input
                      v-if="!editing.phone"
                      :value="form.phone"
                      readonly
                      placeholder="未绑定"
                    >
                    <input
                      v-else
                      v-model="editData.phone"
                      type="text"
                      placeholder="请输入新手机号"
                    >
                    <div class="input-actions">
                      <span v-if="!editing.phone" class="input-icon" @click="startEdit('phone')"><i class="fas fa-pen-to-square"></i></span>
                      <span v-else class="input-icon cancel" @click="cancelEdit('phone')"><i class="fas fa-rotate-left"></i></span>
                    </div>
                  </div>
                  <div v-if="editing.phone" class="code-row">
                    <input v-model="editData.phoneCode" type="text" placeholder="请输入验证码" class="code-input">
                    <button
                      class="code-btn"
                      :disabled="codeCooldown.phone > 0 || sendingCode.phone"
                      @click="sendCode('phone')"
                    >
                      {{ sendingCode.phone ? '发送中…' : (codeCooldown.phone > 0 ? codeCooldown.phone + 's' : '获取验证码') }}
                    </button>
                    <button class="code-btn save" :disabled="saving.phone" @click="savePhone">
                      {{ saving.phone ? '保存中…' : '保存' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="info-group">
              <div class="info-row">
                <div class="label">原密码</div>
                <input v-model="pwd.old" type="password" maxlength="32" placeholder="请输入原密码">
              </div>
              <div class="info-row">
                <div class="label">新密码</div>
                <input v-model="pwd.new" type="password" maxlength="32" placeholder="8-32 位，含字母和数字">
              </div>
              <div class="info-row">
                <div class="label">确认新密码</div>
                <input v-model="pwd.confirm" type="password" maxlength="32" placeholder="再次输入新密码">
              </div>
              <p class="pwd-tip">新密码长度 8-32 位，需同时包含字母和数字，可包含 @$!%*?&</p>
              <p v-if="pwdError" class="error-text">{{ pwdError }}</p>
              <button class="update-pwd" :disabled="pwdLoading" @click="changePassword">
                {{ pwdLoading ? '处理中…' : '更新密码' }}
              </button>
            </div>

            <!--
              注销账号：刻意放在密码区下面、且用红色弱化按钮 ——
              这是不可逆操作，不能和「保存」类按钮长得一样。
              二次确认用 MessageBox.prompt 让用户重新输入密码，而不是简单点「确定」。
            -->
            <div class="info-group danger-zone">
              <div class="danger-head">注销账号</div>
              <p class="danger-desc">
                注销后账号<b>立即停用</b>：无法再登录，所有设备退出登录，昵称改为「已注销用户」。
                手机号、邮箱等身份信息会在保留期（默认 30 天）内继续保存，用于误操作找回与安全追溯，
                期满后自动清空。<b>该操作不可恢复。</b>
                <a href="/privacy" target="_blank" rel="noopener">查看隐私政策</a>
              </p>
              <button class="danger-btn" :disabled="deleting" @click="confirmDeleteAccount">
                {{ deleting ? '注销中…' : '注销账号' }}
              </button>
            </div>
          </template>

          <!-- 界面设置 -->
          <template v-if="activeSection === 'ui'">
            <div class="item">
              <div class="label">主题颜色</div>
              <div class="colors">
                <div
                  v-for="c in themes"
                  :key="c"
                  class="color"
                  :class="{ active: form.theme === c }"
                  :style="{ background: c }"
                  @click="form.theme = c"
                ></div>
              </div>
            </div>

            <div class="item wallpaper-item">
              <div class="label">背景壁纸</div>
              <div class="wallpaper-box">
                <div class="wallpapers">
                  <div
                    v-for="url in wallpaperList"
                    :key="url"
                    class="wall"
                    :class="{ active: form.wallpaper === url }"
                    @click="form.wallpaper = url"
                  >
                    <img :src="resolveImageUrl(url)" alt="wallpaper">
                    <button
                      v-if="isUserWallpaper(url)"
                      class="wall-del"
                      type="button"
                      title="删除该背景图"
                      @click.stop="removeWallpaper(url)"
                    >
                      <i class="fas fa-xmark"></i>
                    </button>
                  </div>
                </div>
                <div class="wallpaper-row">
                  <button
                    @click="$refs.wallpaperInput.click()"
                    :disabled="savingWallpaper"
                  >
                    {{ savingWallpaper ? '上传中…' : '上传背景图' }}
                  </button>
                  <input v-model="form.wallpaper" placeholder="或输入图片 URL">
                  <input
                    ref="wallpaperInput"
                    type="file"
                    accept="image/*"
                    style="display:none"
                    @change="onWallpaperChange"
                  >
                </div>
                <p class="pwd-tip wallpaper-tip">每个账号最多保留 3 张背景图，超过 3 张时再次上传会自动替换最早上传的一张。</p>
              </div>
            </div>
          </template>

          <!-- 用户动态：个人足迹 / 好友互动 -->
          <template v-if="activeSection === 'activity'">
            <div class="activity-section">
              <ActivityFeed :size="20" />
            </div>
          </template>

          <!-- 我的成就：徽章墙 -->
          <template v-if="activeSection === 'achievements'">
            <div class="achievements-section">
              <AchievementsPage :focus-code="achievementFocusCode" />
            </div>
          </template>
        </div>

      </div>
    </div>

    <div class="toast" :class="{ show: toastMsg }">{{ toastMsg }}</div>
  </div>
</template>

<script>
import {
  getMe,
  sendUpdateVerifyCode,
  updateNickname,
  updateEmail,
  updatePhone,
  updatePassword,
  uploadAvatar,
  updateAvatar,
  uploadBackground,
  deleteBackground,
  listBackgrounds,
  deleteAccount,
  logout as logoutApi
} from '@/api/user'
import { MessageBox } from 'element-ui'
import router from '@/router'
import { resolveAvatarUrl, resolveImageUrl } from '@/utils/avatar.js'
import ActivityFeed from './ActivityFeed.vue'
import AchievementsPage from '@/components/achievement/AchievementsPage.vue'
import { achievementStore, consumeOpenRequest } from '@/components/achievement/achievementStore.js'
import {
  profileState,
  saveProfile,
  syncUserLocalStorage,
  resetProfileState
} from './profileStorage'

// 与后端 ChangePasswordCmd 的 @Pattern 保持一致：8-32 位，字母+数字，允许 @$!%*?&
const PASSWORD_PATTERN = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,32}$/

const THEMES = ['#5b9147', '#55784c', '#7da35a', '#d5a94a', '#9c8b6a']
const DEFAULT_HEADER = 'https://image.suisuisu.cn/upload/2607/d1b502465354b537.png'
// 与后端 UpyunService.BG_MAX_COUNT 保持一致：单账号最多保留 3 张背景图
const BG_MAX_COUNT = 3
const PRESET_WALLPAPERS = [
  DEFAULT_HEADER,
  'https://picsum.photos/seed/tbwall1/300/200',
  'https://picsum.photos/seed/tbwall2/300/200'
]

export default {
  name: 'ProfilePage',
  components: { ActivityFeed, AchievementsPage },
  data() {
    return {
      activeSection: 'profile',
      // 各子页面的标题 / 副标题（「我的成就」由 AchievementsPage 自带，故不在此列）
      sectionMeta: {
        profile: { title: '个人资料', subtitle: '管理你的账号信息与安全设置' },
        ui: { title: '界面设置', subtitle: '挑选喜欢的主题色与背景壁纸' },
        activity: { title: '用户动态', subtitle: '你的记录，与好友的互动' }
      },
      // 从解锁浮窗「查看成就」跳转过来时要定位的成就编码
      achievementFocusCode: '',
      form: profileState,
      pwd: {
        old: '',
        new: '',
        confirm: ''
      },
      pwdError: '',
      pwdLoading: false,
      // 注销账号进行中（防重复点击；注销是不可逆操作，双击发两次请求很危险）
      deleting: false,
      themes: THEMES,
      toastMsg: '',
      toastTimer: null,
      // 行内编辑状态
      editing: { nickname: false, email: false, phone: false },
      editData: {
        nickname: '',
        email: '',
        emailCode: '',
        phone: '',
        phoneCode: ''
      },
      saving: { nickname: false, email: false, phone: false },
      savingWallpaper: false,
      deletingWallpaper: false,
      sendingCode: { email: false, phone: false },
      codeCooldown: { email: 0, phone: 0 },
      cooldownTimer: null,
      // 头像加载失败过一次就回退首字母；换头像后由下面的 watch 重新放行
      avatarFailed: false
    }
  },
  computed: {
    /** 当前子页面的标题 / 副标题；「我的成就」返回 null（它自带标题栏）。 */
    currentSectionMeta() {
      return this.sectionMeta[this.activeSection] || null
    },
    firstChar() {
      const name = this.form.nickname || '用'
      return name.charAt(0)
    },
    /** 是否被请求打开「我的成就」（来自解锁浮窗的「查看成就」）。 */
    achievementOpenRequest() {
      return achievementStore.pendingOpen
    },
    /**
     * 又拍云只提供 http，站点是全站 https，直接用原地址会被浏览器按混合内容拦掉，
     * 统一走 resolveAvatarUrl() 换成后端同源 https 代理地址。
     */
    avatarSrc() {
      return resolveAvatarUrl(this.form.avatar)
    },
    wallpaperList() {
      const userBgs = Array.isArray(this.form.userWallpapers) ? this.form.userWallpapers : []
      const list = [...userBgs]
      for (const p of PRESET_WALLPAPERS) {
        if (!list.includes(p)) list.push(p)
      }
      const current = (this.form.wallpaper || '').trim()
      if (current && !list.includes(current)) list.unshift(current)
      return list
    }
  },
  watch: {
    // 换头像 / 从服务端拉到新头像后重新允许加载，避免上一次失败把头像永久锁在首字母
    avatarSrc() {
      this.avatarFailed = false
    },
    // 已经停在个人中心时又从解锁浮窗点了「查看成就」：就地切换分区并定位
    achievementOpenRequest(val) {
      if (val) this.consumeAchievementRequest()
    }
  },
  created() {
    this.loadUserFromServer()
    this.loadBackgrounds()
    // 从成就解锁浮窗「查看成就」跳转而来：直接切到「我的成就」并定位到刚解锁的成就
    this.consumeAchievementRequest()
  },
  beforeDestroy() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer)
  },
  methods: {
    // 模板里要用（<img :src="resolveImageUrl(url)">），需要挂到实例上
    resolveImageUrl,
    /** 打开「我的成就」；手动进入时清掉定位标记，避免重复高亮上一次的成就 */
    openAchievements() {
      this.achievementFocusCode = ''
      this.activeSection = 'achievements'
    },
    /** 消费「打开我的成就」请求（跳转过来 / 已在页面时由 watch 触发） */
    consumeAchievementRequest() {
      const req = consumeOpenRequest()
      if (req.open) {
        this.achievementFocusCode = req.focusCode
        this.activeSection = 'achievements'
      }
    },
    showToast(msg) {
      this.toastMsg = msg
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toastMsg = ''
      }, 2200)
    },
    async loadUserFromServer() {
      try {
        const res = await getMe()
        if (res && res.success && res.data) {
          const user = res.data
          this.form.nickname = user.nickname || this.form.nickname || '用户'
          this.form.email = user.email || this.form.email || ''
          this.form.phone = user.phone || ''
          // 以服务端为准：服务端明确返回空（用户清了头像）时也要把本地缓存清掉，
          // 只有字段缺失（未返回该字段）时才保留本地值，避免误清空。
          if (user.avatarUrl !== undefined) {
            this.form.avatar = user.avatarUrl || ''
          }
        }
      } catch (e) {
        // 未登录或网络异常时静默使用本地缓存
      }
    },
    /** 从服务端拉取已上传的背景图列表，保证多设备下数量/删除按钮与后端一致 */
    async loadBackgrounds() {
      try {
        const res = await listBackgrounds()
        if (res && res.success && Array.isArray(res.data)) {
          this.form.userWallpapers = res.data
        }
      } catch (e) {
        // 未登录或网络异常时静默沿用本地缓存
      }
    },
    /* ---- 行内编辑 ---- */
    startEdit(field) {
      this.editing[field] = true
      if (field === 'nickname') {
        // 昵称可基于当前值微调
        this.editData.nickname = this.form.nickname || ''
      } else if (field === 'email') {
        // form.email 是后端返回的脱敏值（如 u***@example.com），不能直接改；
        // 修改邮箱必须输入全新的邮箱，故清空输入框由占位符提示
        this.editData.email = ''
        this.editData.emailCode = ''
      } else if (field === 'phone') {
        // form.phone 是后端返回的脱敏值（如 138****8000），不能直接改；
        // 修改手机号必须输入全新的手机号，故清空输入框由占位符提示
        this.editData.phone = ''
        this.editData.phoneCode = ''
      }
    },
    cancelEdit(field) {
      this.editing[field] = false
      if (field === 'email') {
        this.editData.emailCode = ''
      } else if (field === 'phone') {
        this.editData.phoneCode = ''
      }
    },
    async sendCode(type) {
      const target = type === 'email' ? this.editData.email : this.editData.phone
      if (!target) {
        this.showToast(type === 'email' ? '请先输入新邮箱' : '请先输入新手机号')
        return
      }
      if (type === 'email' && !/^\S+@\S+\.\S+$/.test(target)) {
        this.showToast('邮箱格式不正确')
        return
      }
      if (type === 'phone' && !/^1[3-9]\d{9}$/.test(target)) {
        this.showToast('手机号格式不正确')
        return
      }

      this.sendingCode[type] = true
      try {
        const res = await sendUpdateVerifyCode(target, type)
        if (res && res.success) {
          this.showToast('验证码已发送')
          this.startCooldown(type)
        } else {
          this.showToast((res && res.message) || '验证码发送失败')
        }
      } catch (e) {
        this.showToast('网络异常，请稍后再试')
      } finally {
        this.sendingCode[type] = false
      }
    },
    startCooldown(type) {
      this.codeCooldown[type] = 60
      if (!this.cooldownTimer) {
        this.cooldownTimer = setInterval(() => {
          let active = false
          for (const key of ['email', 'phone']) {
            if (this.codeCooldown[key] > 0) {
              this.codeCooldown[key]--
              if (this.codeCooldown[key] > 0) active = true
            }
          }
          if (!active) {
            clearInterval(this.cooldownTimer)
            this.cooldownTimer = null
          }
        }, 1000)
      }
    },
    async saveNickname() {
      const nickname = (this.editData.nickname || '').trim()
      if (!nickname) {
        this.showToast('昵称不能为空')
        return
      }
      this.saving.nickname = true
      try {
        const res = await updateNickname(nickname)
        if (res && res.success && res.data) {
          this.form.nickname = res.data.nickname
          this.editing.nickname = false
          this.showToast('昵称已更新')
        } else {
          this.showToast((res && res.message) || '更新失败')
        }
      } catch (e) {
        this.showToast('网络异常，请稍后再试')
      } finally {
        this.saving.nickname = false
      }
    },
    async saveEmail() {
      const email = (this.editData.email || '').trim()
      const code = (this.editData.emailCode || '').trim()
      if (!email) {
        this.showToast('请输入新邮箱')
        return
      }
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        this.showToast('邮箱格式不正确')
        return
      }
      if (!/^\d{6}$/.test(code)) {
        this.showToast('请输入 6 位数字验证码')
        return
      }
      this.saving.email = true
      try {
        const res = await updateEmail(email, code)
        if (res && res.success && res.data) {
          this.form.email = res.data.email
          this.editing.email = false
          this.codeCooldown.email = 0
          this.showToast('邮箱已更新')
        } else {
          this.showToast((res && res.message) || '更新失败')
        }
      } catch (e) {
        this.showToast('网络异常，请稍后再试')
      } finally {
        this.saving.email = false
      }
    },
    async savePhone() {
      const phone = (this.editData.phone || '').trim()
      const code = (this.editData.phoneCode || '').trim()
      if (!phone) {
        this.showToast('请输入新手机号')
        return
      }
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        this.showToast('手机号格式不正确')
        return
      }
      if (!/^\d{6}$/.test(code)) {
        this.showToast('请输入 6 位数字验证码')
        return
      }
      this.saving.phone = true
      try {
        const res = await updatePhone(phone, code)
        if (res && res.success && res.data) {
          this.form.phone = res.data.phone
          this.editing.phone = false
          this.codeCooldown.phone = 0
          this.showToast('手机号已更新')
        } else {
          this.showToast((res && res.message) || '更新失败')
        }
      } catch (e) {
        this.showToast('网络异常，请稍后再试')
      } finally {
        this.saving.phone = false
      }
    },
    /* ---- 保存本地设置（头像/壁纸/主题） ---- */
    saveProfile() {
      this.form.wallpaper = (this.form.wallpaper || '').trim()
      try {
        saveProfile(this.form)
        syncUserLocalStorage(this.form)
        this.showToast('设置已保存')
      } catch (e) {
        this.showToast(e.message || '保存失败')
      }
    },
    async onAvatarChange(e) {
      const file = e.target.files[0]
      if (!file) return
      try {
        const uploadRes = await uploadAvatar(file)
        if (!uploadRes || !uploadRes.success || !uploadRes.data) {
          this.showToast((uploadRes && uploadRes.message) || '头像上传失败')
          return
        }
        const updateRes = await updateAvatar(uploadRes.data)
        if (updateRes && updateRes.success) {
          this.form.avatar = uploadRes.data
          this.saveProfile()
          this.showToast('头像已更新')
        } else {
          this.showToast((updateRes && updateRes.message) || '头像保存失败')
        }
      } catch (err) {
        this.showToast('头像上传失败')
      }
      e.target.value = ''
    },
    async onWallpaperChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      if (!file.type || !file.type.startsWith('image/')) {
        this.showToast('请选择图片文件')
        e.target.value = ''
        return
      }
      // 已达上限时先提醒：继续上传会替换掉最早上传的一张（与后端 FIFO 淘汰逻辑一致）
      const existing = Array.isArray(this.form.userWallpapers) ? this.form.userWallpapers : []
      if (existing.length >= BG_MAX_COUNT) {
        try {
          await MessageBox.confirm(
            `每个账号最多保留 ${BG_MAX_COUNT} 张背景图。继续上传将自动替换掉最早上传的一张，是否继续？`,
            '提示',
            {
              confirmButtonText: '继续上传',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
        } catch (cancelAction) {
          // 用户取消，重置文件选择框
          e.target.value = ''
          return
        }
      }
      this.savingWallpaper = true
      try {
        const res = await uploadBackground(file)
        if (!res || !res.success || !res.data) {
          this.showToast((res && res.message) || '背景图上传失败')
          return
        }
        const data = res.data
        this.form.wallpaper = data.current
        this.form.userWallpapers = Array.isArray(data.backgrounds) ? data.backgrounds : []
        this.saveProfile()
        this.showToast('背景图已更新')
      } catch (err) {
        this.showToast('背景图上传失败')
      } finally {
        this.savingWallpaper = false
      }
      e.target.value = ''
    },
    /** 该背景图是否为当前用户上传的（预设图不允许删除） */
    isUserWallpaper(url) {
      return Array.isArray(this.form.userWallpapers) && this.form.userWallpapers.includes(url)
    },
    /** 删除某张已上传的背景图（同时删除又拍云文件） */
    async removeWallpaper(url) {
      if (this.deletingWallpaper) return
      try {
        await MessageBox.confirm(
          '确定要删除这张背景图吗？删除后无法恢复。',
          '删除背景图',
          {
            confirmButtonText: '删除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
      } catch (cancelAction) {
        return
      }
      this.deletingWallpaper = true
      try {
        const res = await deleteBackground(url)
        if (!res || !res.success) {
          this.showToast((res && res.message) || '删除失败')
          return
        }
        const remaining = Array.isArray(res.data) ? res.data : []
        this.form.userWallpapers = remaining
        // 若删除的正是当前选中的壁纸，回退到剩余第一张或默认图
        if (this.form.wallpaper === url) {
          this.form.wallpaper = remaining[0] || DEFAULT_HEADER
        }
        this.saveProfile()
        this.showToast('背景图已删除')
      } catch (e) {
        this.showToast('删除失败，请稍后再试')
      } finally {
        this.deletingWallpaper = false
      }
    },
    /**
     * 修改密码
     *
     * 真正的新旧密码校验与落库都在后端完成（POST /api/v1/users/password），
     * 前端只做与后端 DTO 一致的格式预校验，避免无效请求。
     * 旧实现只把哈希写进 localStorage，并未通知后端，属于假修改，已废弃。
     */
    async changePassword() {
      this.pwdError = ''
      if (this.pwdLoading) return
      const oldPwd = this.pwd.old
      const newPwd = this.pwd.new
      const confirm = this.pwd.confirm

      if (!oldPwd) {
        this.pwdError = '请输入原密码'
        return
      }
      if (!newPwd) {
        this.pwdError = '请输入新密码'
        return
      }
      if (!confirm) {
        this.pwdError = '请再次输入新密码'
        return
      }
      if (oldPwd.length < 8 || oldPwd.length > 32) {
        this.pwdError = '原密码长度需在 8-32 位之间'
        return
      }
      if (newPwd.length < 8 || newPwd.length > 32) {
        this.pwdError = '新密码长度需在 8-32 位之间'
        return
      }
      if (!PASSWORD_PATTERN.test(newPwd)) {
        this.pwdError = '新密码需同时包含字母和数字，且只能使用字母、数字及 @$!%*?&'
        return
      }
      if (newPwd === oldPwd) {
        this.pwdError = '新密码不能与原密码相同'
        return
      }
      if (newPwd !== confirm) {
        this.pwdError = '两次输入的新密码不一致'
        return
      }

      this.pwdLoading = true
      try {
        const res = await updatePassword(oldPwd, newPwd, confirm)
        if (!res || !res.success) {
          this.pwdError = (res && res.message) || '密码更新失败'
          return
        }
        // 改密即下线：后端已强制该账号所有端下线（含当前端），
        // 所以不再落地任何密码相关信息，直接清本地登录态并跳回首页重新登录
        this.pwd = { old: '', new: '', confirm: '' }
        await MessageBox.alert(
          '密码已更新。为保障账号安全，已退出所有登录设备，请使用新密码重新登录。',
          '修改成功',
          {
            confirmButtonText: '重新登录',
            type: 'warning',
            showClose: false,
            closeOnClickModal: false,
            closeOnPressEscape: false
          }
        ).catch(() => {})
        this.clearLoginState()
        router.go('/')
      } catch (e) {
        this.pwdError = '网络异常，请稍后再试'
      } finally {
        this.pwdLoading = false
      }
    },
    /**
     * 注销账号
     *
     * 二次确认刻意用 MessageBox.prompt 让用户重新输入密码，而不是一个「确定」按钮：
     * 注销不可逆，且 token 是 30 天长效的 —— 手机被人短暂拿到就能毁号，
     * 所以这里必须要求「证明是本人」，后端也会再验一次密码。
     */
    async confirmDeleteAccount() {
      if (this.deleting) return
      let password = ''
      try {
        const { value } = await MessageBox.prompt(
          '注销后账号立即停用、所有设备退出登录，昵称改为「已注销用户」；'
            + '手机号、邮箱等身份信息保留 30 天后自动清空（默认保留期），期间不可恢复使用。'
            + '请输入当前密码确认。',
          '注销账号',
          {
            confirmButtonText: '确认注销',
            cancelButtonText: '取消',
            inputType: 'password',
            inputPlaceholder: '当前密码',
            inputValidator: v => (v && v.trim() ? true : '请输入密码'),
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
          }
        )
        password = (value || '').trim()
      } catch (cancelAction) {
        return
      }

      this.deleting = true
      try {
        const res = await deleteAccount(password)
        if (!res || !res.success) {
          // 后端把原因拼好了（如「原密码错误」），直接展示，不要笼统说「注销失败」
          this.showToast((res && res.message) || '注销失败')
          return
        }
        await MessageBox.alert(
          '账号已注销，感谢你的使用。你的记录将以匿名形式保留，如需一并删除请联系我们。',
          '已注销',
          { confirmButtonText: '返回首页', type: 'success', showClose: false, closeOnClickModal: false }
        ).catch(() => {})
        // 后端已把该账号所有会话踢下线，本地清干净即可（logoutApi 会 401，静默忽略）
        this.clearLoginState()
        router.go('/')
      } catch (e) {
        this.showToast('网络异常，请稍后再试')
      } finally {
        this.deleting = false
      }
    },
    /** 清空本地登录态（token / 用户缓存 / 个人设置缓存），与导航栏「退出登录」保持一致 */
    clearLoginState() {
      // 先注销服务端 token（清 Redis 登录态），失败静默，不阻塞本地登出
      logoutApi().catch(() => {})
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('taskblock-profile')
      resetProfileState()
    }
  }
}
</script>

<style scoped>
.profile-page {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f3f0d8;
  font-family: 'DynaPuff', 'OPPOSans', 'Microsoft YaHei', sans-serif;
  color: #344b32;
}

.profile-page * {
  box-sizing: border-box;
}

.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  gap: 20px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* 左侧 */
.sidebar {
  width: 270px;
  flex-shrink: 0;
  background: #edf3d8;
  border: 3px solid #55784c;
  border-radius: 18px;
  padding: 25px;
  box-shadow: 0 4px 0 #c8c58f;
  overflow-y: auto;
}

.profile {
  text-align: center;
  padding-bottom: 25px;
  border-bottom: 2px solid #9cad73;
}

.avatar {
  width: 95px;
  height: 95px;
  border-radius: 50%;
  border: 4px solid #55784c;
  overflow: hidden;
  margin: auto;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-text {
  font-size: 36px;
  font-weight: bold;
  color: #55784c;
}

.name {
  margin-top: 15px;
  font-size: 20px;
  font-weight: bold;
}

.email {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7d4c;
}

.menu {
  margin-top: 25px;
}

.menu-item {
  height: 45px;
  line-height: 41px;
  padding-left: 20px;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  font-weight: bold;
  border: 2px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.menu-item:hover,
.menu-item.active {
  background: #f6df75;
  border-color: #9b8134;
}

/* 内容 */
.content {
  flex: 1;
  background: #faf8e8;
  border: 3px solid #55784c;
  border-radius: 18px;
  box-shadow: 0 4px 0 #c8c58f;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.title {
  font-size: 28px;
  font-weight: bold;
  color: #344b32;
  padding: 30px 35px 20px;
  flex-shrink: 0;
}

.content-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 35px;
}

.activity-section {
  padding: 20px 0;
}

.achievements-section {
  padding: 20px 0;
}

.item {
  display: flex;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px dashed #b8c28c;
}

.item.no-divider {
  border-bottom: none;
}

/* 资料分组：统一行间距与对齐 */
.info-group {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 0;
  border-bottom: 1px dashed #b8c28c;
}

.info-group:last-of-type {
  border-bottom: none;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 字段容器：包含输入行 + 验证码行 */
.field-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

/* 输入行：input + 图标 */
.input-row {
  position: relative;
  display: flex;
  align-items: center;
  width: 350px;
  max-width: 100%;
}

.input-row input {
  padding-right: 80px;
}

/* 输入框内的操作图标容器 */
.input-actions {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 1;
}

/* 输入框内的操作图标 */
.input-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  cursor: pointer;
  color: #7a8b6a;
  user-select: none;
  line-height: 1;
  transition: color 0.15s ease;
}

.input-icon i {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 15px;
  line-height: 1;
}

/* 调整 fa-rotate-left 的视觉重心，使其与 fa-floppy-disk 水平对齐 */
.input-icon.cancel i.fa-rotate-left {
  margin-top: 1px;
}

.input-icon:hover {
  color: #55784c;
}

.input-icon.save,
.input-icon.cancel {
  color: #7a8b6a;
}

.input-icon.save:hover,
.input-icon.cancel:hover {
  color: #55784c;
}

/* 验证码行 */
.code-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 350px;
  max-width: 100%;
}

.code-input {
  flex: 1;
  min-width: 0;
}

.code-btn {
  height: 42px;
  padding: 0 16px;
  font-size: 13px;
  white-space: nowrap;
  flex-shrink: 0;
}

.code-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.code-btn.save {
  padding: 0 20px;
}

.code-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  cursor: pointer;
  color: #7a8b6a;
  user-select: none;
  line-height: 1;
  transition: color 0.15s ease;
}

.code-icon i {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 15px;
  line-height: 1;
}

.code-icon:hover {
  color: #55784c;
}

.code-icon.cancel i.fa-rotate-left {
  margin-top: 1px;
}

/* 修改头像行：增加底部间距 */
.info-row.avatar-row {
  margin-bottom: 16px;
}

.error-text {
  color: #c45c5c;
  font-size: 14px;
  font-weight: bold;
  margin: 0;
}

/* 密码规则提示 */
.pwd-tip {
  color: #6b7d4c;
  font-size: 13px;
  margin: 0;
}

/* 背景图上传提示 */
.wallpaper-tip {
  margin: 8px 0 0;
}

/* 更新密码按钮：保持自然大小，不被拉伸 */
.update-pwd {
  align-self: flex-start;
}

/* ---------------- 危险操作区（注销账号） ----------------
   与「保存 / 更新」类按钮明显区分：虚线边框分隔 + 红色文字 + 描边按钮。
   不做成实心红块，是为了避免它在页面里过于抢眼而被误点。 */
.danger-zone {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px dashed #e0b4b0;
}

.danger-head {
  font-weight: bold;
  font-size: 16px;
  color: #c0392b;
  margin-bottom: 8px;
}

.danger-desc {
  max-width: 520px;
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.7;
  color: #8a6d6b;
}

.danger-desc b {
  color: #c0392b;
}

.danger-desc a {
  color: #9cad73;
  text-decoration: none;
  margin-left: 4px;
}

.danger-desc a:hover {
  text-decoration: underline;
}

.danger-btn {
  align-self: flex-start;
  padding: 8px 18px;
  font-size: 14px;
  color: #c0392b;
  background: #fff;
  border: 1.5px solid #e0b4b0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.danger-btn:hover:not(:disabled) {
  background: #fdf3f2;
  border-color: #c0392b;
}

.danger-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.label {
  width: 130px;
  flex-shrink: 0;
  font-weight: bold;
  font-size: 16px;
}

input:not([type="file"]) {
  width: 350px;
  max-width: 100%;
  height: 42px;
  border-radius: 10px;
  border: 2px solid #9cad73;
  background: #fff;
  padding: 0 15px;
  font-size: 15px;
  color: #344b32;
  outline: none;
  font-family: inherit;
}

input:focus {
  border-color: #55784c;
}

input[readonly] {
  background: #f0eed8;
  cursor: default;
}

button {
  height: 40px;
  padding: 0 25px;
  background: #f6df75;
  border: 2px solid #9b8134;
  border-radius: 10px;
  font-weight: bold;
  color: #344b32;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.12s ease, background 0.15s ease;
}

button:hover {
  background: #ecd064;
  transform: translateY(-2px);
}

button:active {
  transform: translateY(0);
}

button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

/* 头像 */
.avatar-box {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.small-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #55784c;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.small-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.small-avatar span {
  font-size: 26px;
  font-weight: bold;
  color: #55784c;
}

/* 主题色 */
.colors {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.color {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 0 0 2px #55784c;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.color:hover {
  transform: scale(1.15);
}

.color.active {
  box-shadow: 0 0 0 4px #344b32;
}

/* 壁纸 */
.wallpaper-item {
  align-items: flex-start;
}

.wallpaper-box {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.wallpapers {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}

.wall {
  position: relative;
  width: 160px;
  height: 90px;
  border-radius: 12px;
  overflow: hidden;
  border: 3px solid transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.15s ease, transform 0.15s ease;
}

.wall:hover {
  transform: translateY(-2px);
}

.wall.active {
  border-color: #55784c;
}

.wall img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 已上传背景图上的删除按钮（预设图不显示） */
.wall-del {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(52, 75, 50, 0.72);
  color: #f7f7df;
  font-size: 12px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  transition: background 0.15s ease;
}

.wall-del:hover {
  background: #c45c5c;
  transform: none;
}

.wallpaper-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.wallpaper-row input {
  flex: none;
  width: 350px;
}

/* 保存 */
.save {
  padding: 0;
  text-align: right;
  flex-shrink: 0;
}

/* Toast */
.toast {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  background: #344b32;
  color: #f7f7df;
  padding: 10px 22px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: bold;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s, transform 0.25s;
  z-index: 300;
}

.toast.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

@media (max-width: 900px) {
  .container {
    flex-direction: column;
    align-items: stretch;
  }

  .sidebar {
    width: 100%;
    max-height: 180px;
  }

  .menu {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .menu-item {
    flex: 1;
    min-width: 120px;
    margin-bottom: 0;
    padding-left: 0;
    text-align: center;
  }

  .item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .label {
    width: auto;
  }

  input:not([type="file"]) {
    width: 100%;
  }

  .input-row,
  .code-row {
    width: 100%;
  }

  .wallpapers {
    justify-content: center;
  }

  .wallpaper-row {
    width: 100%;
  }

  .wallpaper-row input {
    width: 100%;
  }
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，布局、尺寸、间距、@media 里的数值全部沿用上面的原值。
   注意：主题色候选块 .color 的 :style 背景、壁纸缩略图 .wall img 都是用户可选数据色，
   这里只重声明它们的描边/选中环，不动数据色本身。 */
.profile-page{
  background:var(--lp-bg,#eef7f0);
  color:var(--lp-text,#26332b);
}
/* 左侧导航 + 右侧内容：两个页面级大面板统一为白底 + 块级重描边 + 柔阴影（原米黄厚涂 + 硬阴影） */
.sidebar{
  background:var(--lp-surface,#fff);
  border:3px solid var(--lp-border-strong,#a5cdb3);
  border-radius:1rem;
  box-shadow:var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}
.content{
  background:var(--lp-surface,#fff);
  border:3px solid var(--lp-border-strong,#a5cdb3);
  border-radius:1rem;
  box-shadow:var(--lp-shadow,0 2px 10px rgba(52,102,68,.06));
}
/* 分隔线：保留原 dashed 样式，只换淡色 */
.profile{
  border-bottom:2px solid var(--lp-border-soft,#eef5f0);
}
.item{
  border-bottom:1px dashed var(--lp-border-soft,#eef5f0);
}
.info-group{
  border-bottom:1px dashed var(--lp-border-soft,#eef5f0);
}
/* 标题 / 副文字 */
.title{
  color:var(--lp-text,#26332b);
}
.email{
  color:var(--lp-text-2,#5f7268);
}
.pwd-tip{
  color:var(--lp-text-2,#5f7268);
}
.error-text{
  color:#d9534f;
}
/* 头像 */
.avatar{
  border:4px solid var(--lp-primary,#4aa46a);
  background:var(--lp-surface,#fff);
}
.avatar-text{
  color:var(--lp-primary,#4aa46a);
}
.small-avatar{
  border:3px solid var(--lp-primary,#4aa46a);
  background:var(--lp-surface,#fff);
}
.small-avatar span{
  color:var(--lp-primary,#4aa46a);
}
/* 左侧菜单：选中/悬停由亮黄 → 主色浅底 + 主色描边 */
.menu-item:hover,
.menu-item.active{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary,#4aa46a);
}
/* 输入框 */
input:not([type="file"]){
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  background:var(--lp-surface,#fff);
  color:var(--lp-text,#26332b);
}
input:focus{
  border-color:var(--lp-primary,#4aa46a);
}
input[readonly]{
  background:var(--lp-surface-2,#f7fbf8);
}
/* 输入框内的操作图标 */
.input-icon{
  color:var(--lp-text-3,#94a59b);
}
.input-icon:hover{
  color:var(--lp-primary,#4aa46a);
}
.input-icon.save,
.input-icon.cancel{
  color:var(--lp-text-3,#94a59b);
}
.input-icon.save:hover,
.input-icon.cancel:hover{
  color:var(--lp-primary,#4aa46a);
}
.code-icon{
  color:var(--lp-text-3,#94a59b);
}
.code-icon:hover{
  color:var(--lp-primary,#4aa46a);
}
/* 按钮：亮黄描边按钮 → 实心主色按钮 */
button{
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary-dark,#2f7a4a);
  border-radius:0.5rem;
  color:#fff;
}
button:hover{
  background:var(--lp-primary-dark,#2f7a4a);
}
/* 主题色候选块：保留用户数据色，只换描边与选中环 */
.color{
  border:3px solid var(--lp-surface,#fff);
  box-shadow:0 0 0 2px var(--lp-primary,#4aa46a);
}
.color.active{
  box-shadow:0 0 0 4px var(--lp-text,#26332b);
}
/* 壁纸缩略图：只换选中描边 */
.wall.active{
  border-color:var(--lp-primary,#4aa46a);
}
/* 壁纸上的删除按钮 */
.wall-del{
  background:rgba(38,51,43,.6);
  color:#fff;
}
.wall-del:hover{
  background:#d9534f;
}
/* Toast */
.toast{
  background:var(--lp-text,#26332b);
  color:var(--lp-surface,#fff);
}
/* 内容区不再有统一的「个人中心」标题（改为各子页面自己的标题），
   这里把原标题占位的上内边距补回内容区，避免内容贴住边框。 */
.content-body{
  padding-top:30px;
}
/* 子页面标题：与 AchievementsPage 的 .ach-title / .ach-subtitle 完全同款，
   并同样靠 20px 上内边距让标题落在内容区顶部 50px 处，四个子页面视线一致。 */
.section-head{
  padding:20px 0 0;
}
.section-title{
  margin:0;
  font-size:24px;
  font-weight:800;
  color:var(--lp-text,#26332b);
}
.section-subtitle{
  margin:6px 0 0;
  font-size:13px;
  color:var(--lp-text-2,#5f7268);
}
</style>
